import React, { useEffect, useRef } from 'react';
import { FiX, FiAlertTriangle, FiInfo, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';

const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium', // 'small', 'medium', 'large', 'xlarge'
  variant = 'default', // 'default', 'danger', 'warning', 'success', 'info'
  closeOnOverlay = true,
  closeOnEscape = true,
  showCloseButton = true,
  className = '',
  overlayClassName = '',
  contentClassName = ''
}) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Cerrar modal con Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (closeOnEscape && e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevenir scroll del body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, closeOnEscape, onClose]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }
    }
  }, [isOpen]);

  const handleOverlayClick = (e) => {
    if (closeOnOverlay && e.target === overlayRef.current) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const modalClasses = [
    'modal',
    `modal--${size}`,
    `modal--${variant}`,
    className
  ].filter(Boolean).join(' ');

  const overlayClasses = [
    'modal-overlay',
    overlayClassName
  ].filter(Boolean).join(' ');

  const contentClasses = [
    'modal-content',
    contentClassName
  ].filter(Boolean).join(' ');

  return (
    <div 
      ref={overlayRef}
      className={overlayClasses}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div ref={modalRef} className={modalClasses}>
        {(title || showCloseButton) && (
          <div className="modal-header">
            {title && (
              <h2 id="modal-title" className="modal-title">
                {title}
              </h2>
            )}
            {showCloseButton && (
              <button
                type="button"
                className="modal-close"
                onClick={onClose}
                aria-label="Cerrar modal"
              >
                <FiX />
              </button>
            )}
          </div>
        )}
        
        <div className={contentClasses}>
          {children}
        </div>
      </div>
    </div>
  );
};

// Modal de confirmación
const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = '¿Estás seguro?',
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  loading = false
}) => {
  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <FiAlertTriangle />;
      case 'warning':
        return <FiAlertCircle />;
      case 'success':
        return <FiCheckCircle />;
      case 'info':
        return <FiInfo />;
      default:
        return <FiAlertTriangle />;
    }
  };

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="small"
      variant={variant}
      closeOnOverlay={!loading}
      closeOnEscape={!loading}
      showCloseButton={!loading}
    >
      <div className="confirm-modal-content">
        <div className="confirm-icon">
          {getIcon()}
        </div>
        
        {message && (
          <div className="confirm-message">
            {message}
          </div>
        )}
        
        <div className="confirm-actions">
          <button
            type="button"
            className="btn btn--outline"
            onClick={onClose}
            disabled={loading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={`btn btn--${variant}`}
            onClick={handleConfirm}
            disabled={loading}
          >
            {loading ? 'Procesando...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Modal de alerta/información
const AlertModal = ({
  isOpen,
  onClose,
  title,
  message,
  variant = 'info',
  buttonText = 'Entendido'
}) => {
  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <FiAlertTriangle className="text-danger" />;
      case 'warning':
        return <FiAlertCircle className="text-warning" />;
      case 'success':
        return <FiCheckCircle className="text-success" />;
      case 'info':
        return <FiInfo className="text-info" />;
      default:
        return <FiInfo className="text-info" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="small"
      variant={variant}
    >
      <div className="alert-modal-content">
        <div className="alert-icon">
          {getIcon()}
        </div>
        
        {message && (
          <div className="alert-message">
            {message}
          </div>
        )}
        
        <div className="alert-actions">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onClose}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

// Hook para manejar modales
const useModal = (initialOpen = false) => {
  const [isOpen, setIsOpen] = React.useState(initialOpen);
  
  const openModal = React.useCallback(() => {
    setIsOpen(true);
  }, []);
  
  const closeModal = React.useCallback(() => {
    setIsOpen(false);
  }, []);
  
  const toggleModal = React.useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  return {
    isOpen,
    openModal,
    closeModal,
    toggleModal
  };
};

export { Modal, ConfirmModal, AlertModal, useModal };
export default Modal;
